import { create } from 'zustand';
import { TR } from './i18n-tr';

/**
 * Tiny i18n: English text is the key, `TR` maps it to Turkish. A missing Turkish entry
 * falls back to English — and `i18n.test.ts` fails the build for any `t('…')` literal in
 * src/ without one, so nothing ships half-translated. `{name}` placeholders are filled from
 * `vars`.
 */

export type Lang = 'tr' | 'en';
const KEY = 'et.lang';

function initialLang(): Lang {
  try {
    const saved = window.localStorage.getItem(KEY);
    if (saved === 'tr' || saved === 'en') return saved;
  } catch {
    // storage unavailable — fall through to the browser language
  }
  return typeof navigator !== 'undefined' && navigator.language.toLowerCase().startsWith('tr') ? 'tr' : 'en';
}

interface LangState {
  lang: Lang;
  setLang: (l: Lang) => void;
}

export const useLang = create<LangState>((set) => ({
  lang: initialLang(),
  setLang: (lang) => {
    try {
      window.localStorage.setItem(KEY, lang);
    } catch {
      // not persisted, still switched for this session
    }
    document.documentElement.lang = lang;
    set({ lang });
  },
}));

export function translate(lang: Lang, text: string, vars?: Record<string, string | number>): string {
  const base = lang === 'tr' ? (TR[text] ?? text) : text;
  return vars ? base.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : base;
}

/** `t` for the current language; components re-render when the language changes. */
export function useT(): (text: string, vars?: Record<string, string | number>) => string {
  const lang = useLang((s) => s.lang);
  return (text, vars) => translate(lang, text, vars);
}

/** Non-React callers (toasts inside handlers). */
export function t(text: string, vars?: Record<string, string | number>): string {
  return translate(useLang.getState().lang, text, vars);
}

export const locale = (): string => (useLang.getState().lang === 'tr' ? 'tr-TR' : 'en-US');
